import type { AxiosError } from 'axios'
import { ElMessage } from 'element-plus'

import i18n from '@/lang'

import type { ErrorContext, ErrorHandlerPort, ErrorHandlerResult } from '../ports/ErrorHandlerPort'

const { t } = i18n.global

type LeafletCreationErrorData = {
  message?: string
}

function getErrorMessage(data: unknown): string {
  if (typeof data !== 'object' || data === null) return ''

  const { message } = data as LeafletCreationErrorData

  return message || ''
}

/**
 * Handles specific leaflet creation errors with known patterns
 * Provides user-friendly messages for common leaflet creation issues
 */
export class LeafletCreationErrorHandler implements ErrorHandlerPort {
  canHandle(error: AxiosError, context: ErrorContext): boolean {
    const message = getErrorMessage(error.response?.data)

    return (
      context.url?.includes('/leaflets') === true &&
      context.method?.toLowerCase() === 'post' &&
      (message.includes('There is already leaflet') || message.includes('Failed to process'))
    )
  }

  async handleError(error: AxiosError): Promise<ErrorHandlerResult> {
    const message = getErrorMessage(error.response?.data)
    let formattedMessage: string

    if (message.includes('There is already leaflet')) {
      formattedMessage = this.handlePriorityConflictError(message)
    } else if (message.includes('Failed to process')) {
      formattedMessage = t('notification.failedToProcessFile')
    } else {
      formattedMessage = message
    }

    return {
      handled: true,
      shouldShowToast: true,
      message: formattedMessage,
      shouldReject: true,
      customAction: () => {
        ElMessage({
          message: `${t('notification.error')}: ${formattedMessage}`,
          type: 'error',
          grouping: true,
          showClose: true,
          duration: 0
        })
      }
    }
  }

  private handlePriorityConflictError(message: string): string {
    const betweenBracketsRegex = /\[(.*?)]/g
    const phrases = message.match(betweenBracketsRegex)?.map(phrase => phrase.slice(1, phrase.length - 1))
    const os = message.split(' ').find((word, i, arr) => arr[i - 1] === 'for')

    if (phrases && phrases.length >= 2 && os) {
      return t('notification.priorityUnavailable', {
        os,
        priorityNumber: phrases[1],
        naturalId: phrases[0]
      })
    }

    return message
  }
}
